import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { QueryableOptionalString } from '../core/optional.js';
import { TimeZone } from '../../time/timezone.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { StoreId } from './store-id.js';
import { SubscriptionStatus } from './subscription-status.js';
import { SubscriptionType } from './subscription-type.js';

export class Subscription extends DataModel {
  static readonly TYPE_ID: string = '5a1bb7dd9a4d461d833f811c19b93a52';

  public readonly uniqueId: string;

  constructor(
    public readonly subscriptionType: SubscriptionType,
    public readonly status: SubscriptionStatus,
    public readonly storeId: StoreId,
    public readonly expirationDate: dayjs.Dayjs,
    public readonly organizationId: string,
    public readonly billingAccountId: string,
    public readonly storeSubscriptionId: QueryableOptionalString,
    public readonly storeSubscriptionStatus: QueryableOptionalString,
  ) {
    super();

    this.uniqueId = `${billingAccountId}:${organizationId}`;
  }

  get $typeId(): string {
    return Subscription.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): Subscription {
    return new Subscription(
        toEnumFromString(MapUtils.get<string>(map, 'subscriptionType'), SubscriptionType),
        toEnumFromString(MapUtils.get<string>(map, 'status'), SubscriptionStatus),
        toEnumFromString(MapUtils.get<string>(map, 'storeId'), StoreId),
        context.decodeDate(MapUtils.get<number>(map, 'expirationDate')),
        MapUtils.get<string>(map, 'organizationId'),
        MapUtils.get<string>(map, 'billingAccountId'),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'storeSubscriptionId')),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'storeSubscriptionStatus')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'subscriptionType': fromEnumAsString(this.subscriptionType),
      'status': fromEnumAsString(this.status),
      'storeId': fromEnumAsString(this.storeId),
      'expirationDate': context.encodeDate(this.expirationDate),
      'organizationId': this.organizationId,
      'billingAccountId': this.billingAccountId,
      'storeSubscriptionId': this.storeSubscriptionId.queryableValue,
      'storeSubscriptionStatus': this.storeSubscriptionStatus.queryableValue,
    };
  }

  clone(): this {
    return new Subscription(
        this.subscriptionType,
        this.status,
        this.storeId,
        TimeZone.cloneDate(this.expirationDate),
        this.organizationId,
        this.billingAccountId,
        this.storeSubscriptionId.clone(),
        this.storeSubscriptionStatus.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.subscriptionType === this.subscriptionType &&
        other.status === this.status &&
        other.storeId === this.storeId &&
        other.expirationDate === this.expirationDate &&
        other.organizationId === this.organizationId &&
        other.billingAccountId === this.billingAccountId &&
        isSameData(other.storeSubscriptionId, this.storeSubscriptionId) &&
        isSameData(other.storeSubscriptionStatus, this.storeSubscriptionStatus);
  }
}

export class SubscriptionBuilder extends DataModelBuilder<Subscription> {
  build(context: SerializationContext, map: SerializedData): Subscription {
    return Subscription.fromMap(context, map);
  }
}
